import { Button, Text } from '@mantine/core';
import { Icon, Select } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import type { CopilotMode } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

import 'app/modules/wallforming/custom-styles/customSelectStyle.css';

interface CopilotPromptComposerProps {
    error: string | null;
    isSending: boolean;
    modelName: string | null;
    mode: CopilotMode;
    prompt: string;
    onCancel: () => void;
    onModeChange: (mode: CopilotMode) => void;
    onPromptChange: (prompt: string) => void;
    onSend: () => void;
}

export const CopilotPromptComposer = ({
    error,
    isSending,
    modelName,
    mode,
    prompt,
    onCancel,
    onModeChange,
    onPromptChange,
    onSend
}: CopilotPromptComposerProps) => {
    const { t } = useTranslation();

    return (
        <div className="shrink-0 border-t border-border-light bg-background-default p-3">
            {error && (
                <Text c="red" mb="xs" role="alert" size="sm">
                    {error}
                </Text>
            )}
            <textarea
                aria-label={t('copilotPromptPlaceholder')}
                className="h-25 w-full resize-none whitespace-pre-wrap wrap-break-word border border-border-light bg-background-default px-2 py-1.5 text-[13px] text-text-default outline-none focus:border-border-accent [hyphens:auto]"
                placeholder={t('copilotPromptPlaceholder')}
                rows={3}
                value={prompt}
                onChange={(event) => onPromptChange(event.currentTarget.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        onSend();
                    }
                }}
            />
            <div className="mt-2 grid min-w-0 grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] items-center gap-x-2 gap-y-1">
                <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-2">
                    <label
                        className="shrink-0 text-xs font-medium text-text-light uppercase"
                        htmlFor="copilot-mode-select"
                    >
                        {t('copilotMode')}:
                    </label>
                    <Select
                        aria-label={t('copilotMode')}
                        className="custom-select nav-select"
                        classNames={{ dropdown: 'nav-select-dropdown' }}
                        comboboxProps={{ withinPortal: false }}
                        data={[
                            { label: t('copilotAskMode'), value: 'ask' },
                            { label: t('copilotAgentMode'), value: 'agent' }
                        ]}
                        id="copilot-mode-select"
                        size="xs"
                        value={mode}
                        onChange={(value) => {
                            if (value === 'ask' || value === 'agent') {
                                onModeChange(value);
                            }
                        }}
                    />
                </div>
                <div className="col-start-1 row-start-2 flex min-w-0 items-center gap-1 text-xs text-text-light">
                    <span className="shrink-0 font-medium uppercase">{t('copilotModel')}:</span>
                    <Text c="dimmed" className="min-w-0 truncate font-bold" title={modelName ?? undefined}>
                        {modelName ?? t('copilotModelUnavailable')}
                    </Text>
                </div>
                {isSending ? (
                    <Button className="col-start-2 row-start-2" color="gray" variant="light" onClick={onCancel}>
                        {t('copilotCancel')}
                    </Button>
                ) : (
                    <Button
                        className="col-start-2 row-start-2"
                        disabled={!prompt.trim()}
                        leftSection={<Icon aria-hidden="true" className="icon mr-2" icon="send" size="lg" />}
                        onClick={onSend}
                    >
                        {t('copilotSend')}
                    </Button>
                )}
            </div>
        </div>
    );
};
